// App Bootstrap
import { loadAndInitializeWorkers, initDataPreFetcher } from "../../utilities/AppBootstrapUtils";
// Constants
import { AppRootElementId, ClientFlavor } from "@ms/uno-constants/lib/local/AppConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { ErrorTypeOption } from "@ms/uno-constants/lib/local/NotificationConstants";
import { MarkerTag } from "@ms/uno-telemetry/lib/local/performance/constants/PerformanceMarkerConstants";
import { LoggingStrings } from "@ms/uno-constants/lib/local/LoggingConstants";
// Controls
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { CreateTaskFromMessageShell } from "@ms/uno-shell/lib/local/controls/createTaskFromMessageShell/CreateTaskFromMessageShell";
import { UnoShell } from "@ms/uno-shell/lib/local/controls/unoShell/UnoShell";
// Factories
import { TeamsActionCreatorFactory } from "./factories/TeamsActionCreatorFactory";
import { TeamsStoreFactory } from "./factories/TeamsStoreFactory";
// HostAdaptors
import { FailedReason } from "@ms/uno-hostadaptors/lib/local/IHostAdaptor";
import { TeamsAdaptor } from "@ms/uno-hostadaptors/lib/local/metaOS/teams/TeamsAdaptor";
import { User } from "@ms/uno-models/lib/local/client/user/User";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { WebPlatformPerformanceMarker } from "@ms/uno-telemetry/lib/local/performance/WebPlatformPerformanceMarker";
// Utilities
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { extractEntityRoutingIdFromSubRoute, logNotificationInfo } from "@ms/uno-routing";
import { sleep } from "@ms/uno-utilities/lib/local/ControlFlowUtilities";
import { getUnoMainContentId } from "@ms/uno-utilities/lib/local/IdUtilities";
import { getLocalStorage } from "@ms/uno-utilities/lib/local/storage/StorageUtilities";
import React from "react";
import * as ReactDOM from "react-dom";
import { ComponentLoader } from "../../utilities/loader/ComponentLoader";
import { configureHostAdaptor, loadWorkersAndRegisterTheme, registerThemeChange, setupProviders, setupWorkerLoaderAndUnoShellProps, } from "../../utilities/MetaOSAppBootstrapSetupUtils";
/**
 * The production bootstrap function
 */
export async function bootstrap() {
    // Set up performance Marker and mark bootstrap
    const performanceMarker = new WebPlatformPerformanceMarker();
    performanceMarker.mark(MarkerTag.AppBootstrap);
    const hostAdaptor = new TeamsAdaptor(performanceMarker, LoggingStrings.ProductName);
    const { loggers, configProvider, context, initialRoute, initialRouteFromDeepLink, telemetryLoggers } = await configureHostAdaptor(hostAdaptor, performanceMarker);
    // Trigger the data pre-fetch
    if (configProvider().flights.EnableTriggeringMetaOSDataPreFetcherFromBootstrap) {
        initDataPreFetcher(initialRouteFromDeepLink, context, hostAdaptor, loggers, configProvider);
    }
    const clientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
    // Setup persistent storage
    const persistentStorage = getLocalStorage(context.userId, loggers.traceLogger, configProvider);
    const user = User.fromUserContext(context);
    const loader = new ComponentLoader(loggers);
    const storeFactory = new TeamsStoreFactory(loader, context, user, initialRoute, loggers, persistentStorage, configProvider, initialRouteFromDeepLink != null, null);
    const { agentProviders, dispatcher, lmProviders, requestPrioritizer, serviceProviders, storeProviders } = await setupProviders(configProvider, context, EngagementTelemetryConstants.TeamsApp, initialRoute, hostAdaptor, loader, loggers, storeFactory);
    const acFactory = new TeamsActionCreatorFactory(loader, dispatcher, lmProviders, storeProviders, loggers, configProvider, hostAdaptor);
    const { acProviders, unoShellProps, workerLoader, persistentAsyncStorage } = await setupWorkerLoaderAndUnoShellProps(agentProviders, context, configProvider, dispatcher, hostAdaptor, loader, loggers, lmProviders, acFactory, storeProviders, storeFactory, serviceProviders, telemetryLoggers);
    if (clientFlavor !== ClientFlavor.TaskFromMessage) {
        setupHostCaching(configProvider, hostAdaptor, loggers, storeFactory, acProviders, workerLoader, requestPrioritizer, persistentAsyncStorage, unoShellProps);
    }
    loadWorkersAndRegisterTheme(acProviders, configProvider, context, hostAdaptor, loggers, persistentAsyncStorage, requestPrioritizer, workerLoader);
    // Clear and unregister legacy TT service workers if the flight is enabled
    if (configProvider().flights.EnableLegacyTTServiceWorkerCleanup) {
        clearAndUnregisterLegacyTTServiceWorkers(loggers);
    }
    if (clientFlavor === ClientFlavor.TaskFromMessage) {
        // Render Create task from teams message shell
        ReactDOM.render(React.createElement(CreateTaskFromMessageShell, { dispatcherEventManagement: dispatcher, actionCreatorProviders: acProviders, serviceProviders: serviceProviders, storeProviders: storeProviders, hostAdaptor: hostAdaptor, loggers: loggers, onLoad: () => hostAdaptor.notifyLoaded(), onSuccess: () => {
                if (!configProvider().flights.EnableTeamsNotifySuccessV2) {
                    hostAdaptor.notifyRenderSuccess();
                }
            }, onFail: (message) => hostAdaptor.notifyRenderFailure({ reason: FailedReason.Other, message }), configProvider: configProvider }), document.getElementById(AppRootElementId));
    }
    else if (!configProvider().changeGates.DisableDeferredBootForBackgroundLoad && context.isBackgroundLoad) {
        // If the app is loaded in background, we should complete the bootstrap with a placeholder view.
        // This will avoid the API calls invoked as a side effect from Shell render.
        renderPlaceholderForBackgroundLoad(hostAdaptor);
    }
    else {
        // Render the uno shell if loading the app in foreground
        ReactDOM.render(React.createElement(UnoShell, { ...unoShellProps }), document.getElementById(AppRootElementId));
    }
}
/**
 * Setup load and unload handler for app caching
 * @param configProvider Provider for app configuration
 * @param hostAdaptor The host adaptor
 * @param loggers Loggers
 * @param storeLoader The store loader
 * @param acProviders Action creator providers
 * @param workerLoader The worker loader
 * @param requestPrioritizer The request prioritizer
 * @param persistentAsyncStorage The persistent async storage for Planner app
 * @param unoShellProps The uno shell props used for rendering the uno shell
 */
function setupHostCaching(configProvider, hostAdaptor, loggers, storeLoader, acProviders, workerLoader, requestPrioritizer, persistentAsyncStorage, unoShellProps) {
    if (configProvider().settings.disableAppCaching) {
        return;
    }
    const onLoadCallback = async (newClientFlavor, newRoute, contentUrl, cachedLoadId) => {
        loggers.traceLogger.logTrace(0x1e19b18d /* tag_4g1gn */, TraceLevel.Info, `Teams cached app load. [ClientFlavor=${newClientFlavor}]`);
        // Override client flavor
        const currentClientFlavor = configProvider().sessionMetaData.appMetadata.clientFlavor;
        const clientFlavorChanged = newClientFlavor !== currentClientFlavor;
        AppContext.overrideClientFlavor(newClientFlavor);
        // Entity id before the cached load. Null for cases where entity id is not present such as myDay, myTasks etc.
        // Extract entity id based on the existing route (previous state before the cached load).
        const routeStore = storeLoader.load(Store.Route);
        const oldEntityId = extractEntityRoutingIdFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
        // Entity id after the cached load. Null for cases where entity id is not present such as myDay, myTasks.
        // Undefined for cases when client flavor changes where reboot would happen and determine the new entity id based on persisted route on new client flavor.
        let newEntityId = undefined;
        // If the app was loaded on background, the boot would be resumed on the cached load.
        let bootCompletedOnCachedLoad = false;
        if (clientFlavorChanged) {
            // If there's a client flavor update, re-boot the app to ensure the correct client flavor is used across dependencies
            // When the app is brought out of cache, teams SDK invoke the onLoad handler that the app provids when it registered.
            // The onLoad handler from Teams SDK will pass the new contentUrl. It is up to the app to use this info to navigate to the correct context.
            // Note: Updating the href will trigger the re-boot of the app. With local testing we have seen the JS bundles are being picked up from disk cache.
            // This is required on client change only as other cases are handled with below -
            // 1. Tab <-> Tab switches - Updating the route with uno's routing action creator
            // 2. App <-> App switches - No route change required as the app is loaded with on the same route
            window.location.href = contentUrl;
        }
        else {
            const appContextStore = storeLoader.load(Store.AppContext);
            const unoMainContentId = getUnoMainContentId(appContextStore.getAppInstanceId());
            // Check if the uno main content is already rendered.
            // If the uno main content is not rendered, it means the app is being loaded for the first time after background load.
            const hasBootCompleted = document.getElementById(unoMainContentId) !== null;
            // Render the Uno shell and Initialize the workers if required.
            // This is necessary as the worker is not initialized during deferred shell rendering on background load.
            if (!configProvider().changeGates.DisableDeferredBootForBackgroundLoad && !hasBootCompleted) {
                loggers.traceLogger.logTrace(0x1e19b18c /* tag_4g1gm */, TraceLevel.Info, `Teams cached app load - Rendering UnoShell`);
                loadAndInitializeWorkers(workerLoader, requestPrioritizer, loggers, persistentAsyncStorage, configProvider, hostAdaptor, cachedLoadId);
                ReactDOM.render(React.createElement(UnoShell, { ...unoShellProps }), document.getElementById(AppRootElementId));
                bootCompletedOnCachedLoad = true;
            }
            // If new route is provided within same client flavor (switch between different tabs), navigate to the new route
            if (newRoute != null) {
                logNotificationInfo(newRoute, loggers);
                newEntityId = extractEntityRoutingIdFromSubRoute(newRoute.subRoute, newRoute.subRouteType);
                const routeActionCreator = acProviders.routing();
                routeActionCreator.navigate(newRoute);
            }
            else {
                // For flavors (such as App) where the newRoute is null i.e. no route change required, the entity id remains the same.
                newEntityId = oldEntityId;
            }
            // Update app context and re-register theme change handler.
            // This is not required when client flavor changes as the app will be re-booted and handle them inherently.
            setTimeout(async () => {
                // [WI #9580747]: onLoadCallback is invoked before the viewport is set for the app.
                // Triggering below store update before the viewport is set could cause incorrect rendering of the components.
                const [appContextActionCreator, deltaSyncActionCreator] = await Promise.all([
                    acProviders.appContext(),
                    acProviders.deltaSync(),
                ]);
                // Execute the theme update after a delay to ensure the app has been fully loaded before triggering the update.
                appContextActionCreator.fetchAppContext();
                appContextActionCreator.updateAppBackgroundState(false); // Update background state to false as the app is loaded in foreground
                if (!configProvider().changeGates.DisableStopBackgroundSyncWhenCached) {
                    deltaSyncActionCreator.enableDeltaSync();
                }
            }, 100);
            registerThemeChange(hostAdaptor, acProviders);
            // Call notify success to indicate that the app has been successfully loaded from cache
            // Do not invoke notifyRenderSuccess if uno shell is rendered as part of cached load as it is already invoked with the boot completion.
            if (!bootCompletedOnCachedLoad) {
                hostAdaptor.notifyRenderSuccess();
            }
        }
        if (!bootCompletedOnCachedLoad) {
            loggers.performanceMarker.mark(MarkerTag.CachedLoadComplete, {
                cachedLoadId, // Unique identifier for the particular cached load instance
                bootCompletedOnCachedLoad: "false", // Indicates if the boot was completed on cached load
                oldFlavor: currentClientFlavor, // Client flavor before the cached load
                newFlavor: newClientFlavor, // Client flavor after the cached load
                oldEntityId: oldEntityId ?? "", // Entity id before the cached load. Empty string for cases where entity id is not present.
                newEntityId: newEntityId ?? "", // Entity id after the cached load. Empty string for cases where entity id is not present.
            });
        }
    };
    const onUnloadCallback = async (notifyUnloadSuccess) => {
        // TODO: Invoke the background API on IViewRender once implemented for each view to perform clean up.
        const appContextActionCreator = await acProviders.appContext();
        appContextActionCreator.updateAppBackgroundState(true); // Update background state to true as the app is moving to background
        if (!configProvider().changeGates.DisableStopBackgroundSyncWhenCached) {
            const deltaSyncActionCreator = await acProviders.deltaSync();
            deltaSyncActionCreator.disableDeltaSync(ErrorTypeOption.None);
        }
        notifyUnloadSuccess();
    };
    hostAdaptor.setupHostCaching(onLoadCallback, onUnloadCallback);
}
/**
 * Renders a placeholder view when the app is loaded in background
 * @param hostAdaptor The host adaptor (Teams)
 */
function renderPlaceholderForBackgroundLoad(hostAdaptor) {
    ReactDOM.render(React.createElement(React.Fragment, null), document.getElementById(AppRootElementId));
    // Notify app loaded completion for the background load.
    hostAdaptor.notifyLoaded();
    hostAdaptor.notifyRenderSuccess();
}
/**
 * Sends a message to any running legacy TT service worker registrations to clear their caches and unregisters them
 * @param loggers Loggers to use for logging
 */
async function clearAndUnregisterLegacyTTServiceWorkers(loggers) {
    // The specific command to send to the service worker to clear its caches and unregister
    // ref: https://dev.azure.com/domoreexp/Teamspace/_git/teams-client-tasks?path=/web/ui/common/bootConstants.ts&version=GBdevelop&line=169&lineEnd=171&lineStartColumn=1&lineEndColumn=3&lineStyle=plain&_a=contents
    const unregisterCommand = {
        command: "clearAndUnregister",
    };
    const legacyWorkerPath = "/teamsui/service-worker.js";
    // Check that service workers are supported
    if (!("serviceWorker" in navigator) || typeof navigator.serviceWorker.getRegistrations !== "function") {
        // no-op, service workers are not supported
        // end control flow here
        return;
    }
    // Get all service worker registrations and post the message to unregister them
    try {
        const regs = await navigator.serviceWorker.getRegistrations();
        const legacyRegs = regs.filter((reg) => reg.installing?.scriptURL?.includes(legacyWorkerPath) || reg.active?.scriptURL?.includes(legacyWorkerPath));
        if (legacyRegs.length > 0) {
            await Promise.all(legacyRegs.map(async (reg) => {
                if (reg.installing) {
                    reg.installing.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e19b18b /* tag_4g1gl */, TraceLevel.Info, `Tell legacy ServiceWorker to clear out [state=${reg.installing.state}]`);
                }
                if (reg.active) {
                    reg.active.postMessage(unregisterCommand);
                    loggers.traceLogger.logTrace(0x1e19b18a /* tag_4g1gk */, TraceLevel.Info, `Tell legacy ServiceWorker to clear out [state=${reg.active.state}]`);
                }
                // just wait for 5s to allow the worker to clear caches before unregistering
                await sleep(5000);
                const result = await reg.unregister();
                loggers.traceLogger.logTrace(0x1e19b189 /* tag_4g1gj */, TraceLevel.Info, `Legacy ServiceWorker unregistration done [result=${result}]`);
            }));
            loggers.traceLogger.logTrace(0x1e19b188 /* tag_4g1gi */, TraceLevel.Info, `Legacy ServiceWorker unregistrations done [count=${legacyRegs.length}]`);
        }
    }
    catch (e) {
        loggers.traceLogger.logTrace(0x1e19b187 /* tag_4g1gh */, TraceLevel.Warning, `Legacy ServiceWorker unregistrations failed [reason=${ErrorUtilities.getMessage(e)}]`);
    }
}
